import React, { useState, useEffect, useContext, useCallback} from 'react';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import GrpButton from 'Library/GrpButton/GrpButton';
import {
  getValidationDashboardGlobal,
  getValidationDashboardPerResult,
  getValidationDashboardPerField,
  extractBusinessRulesResults,
  getReportingPeriods,
  getFundsList,
  getFundGroups,
} from 'Services/overviewApi';
import classes from './BusinessRulesDashboard.module.scss';

const CARD_TYPES = [
  { title: 'Errors', ruleResult: 'Error', cssClass: 'errors' },
  { title: 'Warnings', ruleResult: 'Warning', cssClass: 'warnings' },
  { title: 'Success', ruleResult: 'Success', cssClass: 'success' },
];

const DEFAULT_FUND = '0 All Funds';
const DEFAULT_FUND_GROUP = '0 All Fund Groups';

const firstDefined = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

const getFundCode = (fund) => firstDefined(fund, [
  'fundcode', 'FundCode', 'Fund_Code', 'fund_code', 'fundcode_bk', 'FundCode_bk', 'FundCodeBK',
]);

const getFundName = (fund) => firstDefined(fund, [
  'fundname', 'FundName', 'Fund_Name', 'fund_name', 'fund_name_bk', 'Fund_Name_bk',
]);

const getFundGroupCode = (item) => firstDefined(item, [
  'fundgroupcode', 'FundGroupCode', 'Fund_Group_Code', 'fund_group_code',
]);

const getFundGroupName = (item) => firstDefined(item, [
  'fundgroupname', 'FundGroupName', 'Fund_Group_Name', 'fund_group_name',
]);

const getReferenceDate = (item) => firstDefined(item, [
  'Reference_Date', 'reference_date', 'ReferenceDate', 'referenceDate',
]);

const buildFundLabel = (fund) => {
  const name = getFundName(fund);
  const code = getFundCode(fund);
  if (name && code) return `${name} (${code})`;
  return name || code;
};

const buildFundGroupLabel = (group) => {
  const name = getFundGroupName(group);
  const code = getFundGroupCode(group);
  if (name && code) return `${name} (${code})`;
  return name || code;
};

const BusinessRulesDashboard = () => {
  const context = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFund, setSelectedFund] = useState(DEFAULT_FUND);
  const [selectedFundGroup, setSelectedFundGroup] = useState(DEFAULT_FUND_GROUP);
  const [fundOptions, setFundOptions] = useState([]);
  const [fundGroupOptions, setFundGroupOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [cards, setCards] = useState([]);
  const [exporting, setExporting] = useState(false);

  const reportingPeriod = context.state?.selectedReportingDate?.code;
  const regulation = context.state?.selectedRegulation;

  const formatRP = useCallback((rp) => {
    if (!rp) return '';
    if (typeof rp === 'string') return rp;
    return new Date(rp).toISOString().split('T')[0];
  }, []);

  const loadGlobalData = useCallback(async (search, fundGroup, fund) => {
    const rpFormatted = formatRP(reportingPeriod);
    if (!rpFormatted || !regulation) return;

    const fg = (!fundGroup || fundGroup === DEFAULT_FUND_GROUP) ? '' : fundGroup;
    const fc = (!fund || fund === DEFAULT_FUND) ? '' : fund;

    const res = await getValidationDashboardGlobal(rpFormatted, regulation.Name, search, fg, fc);
    const data = res.value || res.Content || [];

    const getCount = (result) => {
      const match = data.find((d) => d.ruleresult === result);
      return match ? match.count : 0;
    };

    return CARD_TYPES.map((ct) => ({
      ...ct,
      count: getCount(ct.ruleResult),
      expanded: false,
      perResult: null,
      loadingPerResult: false,
    }));
  }, [reportingPeriod, regulation, formatRP]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const rpFormatted = formatRP(reportingPeriod);
      if (!rpFormatted || !regulation) return;

      const [cardsData, fundsRes, fundGroupsRes] = await Promise.all([
        loadGlobalData('', DEFAULT_FUND_GROUP, DEFAULT_FUND),
        getFundsList(rpFormatted, regulation.Name, '', ''),
        getFundGroups(),
      ]);

      setCards(cardsData || []);

      const fundsData = fundsRes.value || fundsRes.Content || [];
      const fOpts = fundsData
        .map((f) => {
          const value = getFundCode(f);
          const label = buildFundLabel(f);
          if (!value || !label) return null;
          return { label, value };
        })
        .filter(Boolean);
      fOpts.push({ label: 'All Funds', value: DEFAULT_FUND });
      setFundOptions(fOpts);

      const fgData = fundGroupsRes.value || fundGroupsRes.Content || [];
      const rpStr = rpFormatted;
      const fgOpts = fgData
        .filter((fg) => {
          const referenceDate = getReferenceDate(fg);
          if (!referenceDate) return true;
          return referenceDate.substring(0, 10) === rpStr;
        })
        .map((fg) => {
          const value = getFundGroupCode(fg);
          const label = buildFundGroupLabel(fg);
          if (!value || !label) return null;
          return { label, value };
        })
        .filter(Boolean);
      fgOpts.push({ label: 'All Fund Groups', value: DEFAULT_FUND_GROUP });
      setFundGroupOptions(fgOpts);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load business rules'}</> });
    } finally {
      setLoading(false);
    }
  }, [reportingPeriod, regulation, formatRP, loadGlobalData, context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshCards = async (search, fundGroup, fund) => {
    try {
      setLoading(true);
      const newCards = await loadGlobalData(search, fundGroup, fund);
      setCards(newCards || []);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to refresh'}</> });
    } finally {
      setLoading(false);
    }
  };

  const handleFundChange = async (e) => {
    const fund = e.target.value;
    setSelectedFund(fund);
    setSearchValue('');
    await refreshCards('', selectedFundGroup, fund);
  };

  const handleFundGroupChange = async (e) => {
    const fundGroup = e.target.value;
    setSelectedFundGroup(fundGroup);
    setSelectedFund(DEFAULT_FUND);
    setSearchValue('');
    await refreshCards('', fundGroup, DEFAULT_FUND);

    // Refresh fund list based on fund group
    const rpFormatted = formatRP(reportingPeriod);
    const fundsRes = await getFundsList(rpFormatted, regulation.Name, '', '');
    const fundsData = fundsRes.value || fundsRes.Content || [];
    const fg = (!fundGroup || fundGroup === DEFAULT_FUND_GROUP) ? '' : fundGroup;
    const filtered = fg
      ? fundsData.filter((f) => getFundGroupCode(f) === fg)
      : fundsData;
    const fOpts = filtered
      .map((f) => {
        const value = getFundCode(f);
        const label = buildFundLabel(f);
        if (!value || !label) return null;
        return { label, value };
      })
      .filter(Boolean);
    fOpts.push({ label: 'All Funds', value: DEFAULT_FUND });
    setFundOptions(fOpts);
  };

  const handleSearch = async () => {
    if (searchValue !== '') {
      setSelectedFund(DEFAULT_FUND);
    }
    await refreshCards(searchValue, selectedFundGroup, selectedFund);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSelectedFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleOuterCard = async (index) => {
    const card = cards[index];
    const newExpanded = !card.expanded;

    if (newExpanded && !card.perResult) {
      // Load per-result data
      const updatedCards = [...cards];
      updatedCards[index] = { ...card, expanded: true, loadingPerResult: true };
      setCards(updatedCards);

      try {
        const rpFormatted = formatRP(reportingPeriod);
        const fg = (!selectedFundGroup || selectedFundGroup === DEFAULT_FUND_GROUP) ? '' : selectedFundGroup;
        const fc = (!selectedFund || selectedFund === DEFAULT_FUND) ? '' : selectedFund;

        const res = await getValidationDashboardPerResult(
          rpFormatted, regulation.Name, card.ruleResult, searchValue, fg, fc
        );
        const data = res.value || res.Content || [];
        const perResult = data.map((d) => ({
          ruleField: d.rulefield,
          ruleResult: d.ruleresult,
          count: d.count,
          expanded: false,
          perFieldData: null,
          loadingPerField: false,
        }));

        updatedCards[index] = { ...card, expanded: true, loadingPerResult: false, perResult };
        setCards([...updatedCards]);
      } catch (err) {
        const updatedCards2 = [...cards];
        updatedCards2[index] = { ...card, expanded: true, loadingPerResult: false, perResult: [] };
        setCards(updatedCards2);
      }
    } else {
      const updatedCards = [...cards];
      updatedCards[index] = { ...card, expanded: newExpanded };
      setCards(updatedCards);
    }
  };

  const toggleInnerCard = async (cardIndex, resultIndex) => {
    const card = cards[cardIndex];
    const group = card.perResult[resultIndex];
    const newExpanded = !group.expanded;

    if (newExpanded && !group.perFieldData) {
      // Load per-field grid data
      const updatedCards = [...cards];
      updatedCards[cardIndex].perResult[resultIndex] = { ...group, expanded: true, loadingPerField: true };
      setCards([...updatedCards]);

      try {
        const rpFormatted = formatRP(reportingPeriod);
        const fg = (!selectedFundGroup || selectedFundGroup === DEFAULT_FUND_GROUP) ? '' : selectedFundGroup;
        const fc = (!selectedFund || selectedFund === DEFAULT_FUND) ? '' : selectedFund;

        const res = await getValidationDashboardPerField(
          rpFormatted, regulation.Name, group.ruleResult, group.ruleField, searchValue, fg, fc
        );
        const rawData = res.value || res.Content || [];
        const perFieldData = rawData.map((item) => ({
          Fund: `${item.fund_name ?? ''} ${item.fundcode_bk ?? ''}`,
          ShareClass: `${item.of_full_share_class_name ?? ''} ${item.classcode_bk ?? ''}`,
          Report: item.category,
          Value: item.originalvalue == null ? 'NULL' : item.originalvalue,
          Message: item.rulemessage,
          Result: item.ruleresult,
          Language: !item.iso_language ? 'English' : item.iso_language,
        }));

        updatedCards[cardIndex].perResult[resultIndex] = {
          ...group, expanded: true, loadingPerField: false, perFieldData,
        };
        setCards([...updatedCards]);
      } catch (err) {
        const updatedCards2 = [...cards];
        updatedCards2[cardIndex].perResult[resultIndex] = {
          ...group, expanded: true, loadingPerField: false, perFieldData: [],
        };
        setCards([...updatedCards2]);
      }
    } else {
      const updatedCards = [...cards];
      updatedCards[cardIndex].perResult[resultIndex] = { ...group, expanded: newExpanded };
      setCards([...updatedCards]);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      context.handlers.setToast({ type: 'info', body: <>Downloading Business Rules Dashboard...</> });

      const rpFormatted = formatRP(reportingPeriod);
      const fc = (!selectedFund || selectedFund === DEFAULT_FUND) ? null : selectedFund;
      const fg = (!selectedFundGroup || selectedFundGroup === DEFAULT_FUND_GROUP) ? null : selectedFundGroup;
      const ruleResults = selectedFilters.join(';');

      const fileData = await extractBusinessRulesResults(
        fc, rpFormatted, regulation.Name, ruleResults, searchValue, fg
      );

      const blob = new Blob([fileData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${context.state?.selectedClient?.code || 'export'}_BusinessRules_Output_${rpFormatted.replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      context.handlers.setToast({ type: 'success', body: <>Business Rules Dashboard downloaded.</> });
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Error exporting business rules'}</> });
    } finally {
      setExporting(false);
    }
  };

  const isCardVisible = (ruleResult) => {
    if (selectedFilters.length === 0) return true;
    return selectedFilters.includes(ruleResult);
  };

  const fieldGridColumnDefs = [
    { headerName: 'Fund', field: 'Fund', flex: 1 },
    { headerName: 'Share Class', field: 'ShareClass', flex: 1 },
    { headerName: 'Report', field: 'Report', width: 120 },
    { headerName: 'Value', field: 'Value', flex: 1 },
    { headerName: 'Message', field: 'Message', width: 450 },
    { headerName: 'Result', field: 'Result', width: 120 },
    { headerName: 'Language', field: 'Language', width: 120 },
  ];

  if (loading) {
    return <GrpSpinner />;
  }

  const hasData = cards.some((c) => c.count > 0);

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <div className={classes.fundPicker}>
          {fundGroupOptions.length > 1 && (
            <select
              value={selectedFundGroup}
              onChange={handleFundGroupChange}
              className={classes.select}
            >
              {fundGroupOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          <select
            value={selectedFund}
            onChange={handleFundChange}
            className={classes.select}
          >
            {fundOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className={classes.searchBox}>
          <input
            type="text"
            placeholder="Search Share Classes"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className={classes.searchInput}
          />
        </div>
        <GrpButton
          size="Medium"
          type="Primary"
          text="Export (Excel)"
          onClick={handleExportExcel}
          disabled={!hasData || exporting}
        />
      </div>

      <div className={classes.filterBar}>
        {CARD_TYPES.map((ct) => (
          <label key={ct.ruleResult} className={classes.filterLabel}>
            <input
              type="checkbox"
              value={ct.ruleResult}
              checked={selectedFilters.includes(ct.ruleResult)}
              onChange={handleFilterChange}
            />
            {ct.title}
          </label>
        ))}
      </div>

      <div className={classes.cardsContainer}>
        {cards.map((card, cardIndex) => {
          if (!isCardVisible(card.ruleResult)) return null;
          return (
            <div key={card.ruleResult} className={`${classes.outerCard} ${classes[card.cssClass]}`}>
              <div
                className={classes.outerCardHeader}
                onClick={() => toggleOuterCard(cardIndex)}
              >
                <span className={classes.expandIcon}>
                  {card.expanded ? '▼' : '▶'}
                </span>
                <span className={classes.outerCardTitle}>{card.title}</span>
                <span className={classes.outerCardSubtitle}>{card.count} Business Rules</span>
              </div>

              {card.expanded && (
                <div className={classes.outerCardContent}>
                  {card.loadingPerResult && (
                    <div className={classes.loadingText}>LOADING...</div>
                  )}
                  {!card.loadingPerResult && card.perResult?.length === 0 && (
                    <div className={classes.loadingText}>No Data To Display.</div>
                  )}
                  {!card.loadingPerResult && card.perResult?.map((group, resultIndex) => (
                    <div key={group.ruleField} className={classes.innerCard}>
                      <div
                        className={classes.innerCardHeader}
                        onClick={() => toggleInnerCard(cardIndex, resultIndex)}
                      >
                        <span className={classes.expandIcon}>
                          {group.expanded ? '▼' : '▶'}
                        </span>
                        <span className={classes.innerCardTitle}>{group.ruleField}</span>
                        <span className={classes.badge}>{group.count}</span>
                      </div>

                      {group.expanded && (
                        <div className={classes.innerCardContent}>
                          {group.loadingPerField && (
                            <div className={classes.loadingText}>LOADING...</div>
                          )}
                          {!group.loadingPerField && group.perFieldData && (
                            <div className={`ag-theme-reborn ${classes.innerGrid}`}>
                              <AgGridReact
                                rowData={group.perFieldData}
                                columnDefs={fieldGridColumnDefs}
                                defaultColDef={{
                                  sortable: true,
                                  resizable: true,
                                  filter: true,
                                }}
                                pagination={true}
                                paginationPageSize={20}
                                domLayout="autoHeight"
                                rowHeight={60}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BusinessRulesDashboard;
